/**
 * API envelope unwrap utilities.
 *
 * The standard Plerity backend envelope: { success: boolean, data: T, error?: string }
 * These utilities strip the envelope and (optionally) transform snake_case keys to
 * camelCase.
 *
 * Usage via factory (recommended):
 *   import { createUnwrappedFetcher } from "@plerity/data-access";
 *   // default: envelope unwrap ON, snake→camel ON, silent on envelope errors
 *   const { get, post, put, patch, del } = createUnwrappedFetcher({
 *     baseURL: import.meta.env.VITE_API_URL ?? "",
 *     withCredentials: true,
 *   });
 *
 *   // opt out of transform (e.g. apps that want backend snake_case preserved):
 *   const { get } = createUnwrappedFetcher({ baseURL: "/api", transform: false });
 *
 *   // opt out of unwrap (raw axios `data` body, envelope intact):
 *   const { get } = createUnwrappedFetcher({ baseURL: "/api", unwrap: false });
 *
 *   // throw on { success: false } envelopes instead of silently passing through:
 *   const { get } = createUnwrappedFetcher({
 *     baseURL: "/api",
 *     throwOnEnvelopeError: true,
 *   });
 *
 * Usage standalone:
 *   import { unwrap, snakeToCamelKeys } from "@plerity/data-access";
 *   const result = unwrap<MyType>(responseData);
 *   const raw = unwrap<MyType>(responseData, { transform: false });
 *
 * Audit (2026-05-26): per-app `services/api.ts` get() contracts compared in
 * agents/analysis/useapiquery_fetcher_parity_audit_2026-05-26.md. The
 * `unwrap` / `transform` / `throwOnEnvelopeError` knobs were added so that
 * chat-admin / design / governance / health / reports / research can each
 * configure the shared factory to match their existing semantics.
 */

import type { AxiosRequestConfig } from "./fetcher";
import { createFetcher, type FetcherConfig } from "./fetcher";

/** Shape of a Plerity API envelope response. */
export interface ApiEnvelope<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

/** A custom key transformer. Receives the payload and returns the transformed payload. */
export type KeyTransformer = (data: unknown) => unknown;

/** Options for the standalone `unwrap` helper and the factory. */
export interface UnwrapOptions {
  /**
   * Strip the `{ success, data }` envelope before returning the payload.
   * Default: true. Set false to return the raw response body untouched.
   */
  unwrap?: boolean;
  /**
   * Key transformation policy.
   * - `true` (default): apply `snakeToCamelKeys` recursively.
   * - `false`: no transform — keys reach consumers exactly as the backend sent them.
   * - function: a custom transformer (e.g. for camel→snake on outbound writes).
   */
  transform?: boolean | KeyTransformer;
  /**
   * If true, throw `Error(envelope.error ?? "Request failed")` when the response
   * envelope's `success` is false. Default: false (silent strip — matches
   * pre-2026-05-26 behaviour; the caller is responsible for error checking).
   */
  throwOnEnvelopeError?: boolean;
}

/** Convert a single snake_case string to camelCase */
export function snakeToCamelKey(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

/** Recursively convert all object keys from snake_case to camelCase */
export function snakeToCamelKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(snakeToCamelKeys);
  }
  if (obj !== null && typeof obj === "object" && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[snakeToCamelKey(key)] = snakeToCamelKeys(value);
    }
    return result;
  }
  return obj;
}

/**
 * Detect a Plerity-style envelope: object carrying a `success` key.
 *
 * Lenient detection (aligned with `@plerity/dashboard-shared`'s `unwrapBody`
 * as of the 2026-05-26 useApiQuery blitz): the `data` field is OPTIONAL.
 * Error envelopes from `_shared/error_middleware.py` always carry
 * `{ success: false, error }` and sometimes omit `data` entirely; success
 * envelopes for 204-style endpoints may also omit `data`. Both shapes must
 * be recognised as envelopes so callers can branch on `success`.
 *
 * Pre-2026-05-26 this required BOTH `success` AND `data`, which caused
 * error envelopes without `data: null` to slip through as raw payloads
 * (the design app's `SettingsPage.test.tsx` had to add `data: null` to
 * its error mock as a workaround — see S02 handoff 2026-05-26). The
 * dashboard-shared hook is the SSoT we're aligning to.
 */
function isEnvelope(value: unknown): value is { success: boolean; data?: unknown; error?: string } {
  return value != null && typeof value === "object" && "success" in value;
}

/**
 * Unwrap the standard Plerity API envelope: { success, data: T } → T
 * Also transforms snake_case keys to camelCase recursively by default.
 *
 * Options:
 * - `unwrap` (default true): strip `{ success, data }` when present.
 * - `transform` (default true): apply `snakeToCamelKeys`. Pass `false` to skip
 *   key transformation, or a function for a custom transformer.
 * - `throwOnEnvelopeError` (default false): throw on `{ success: false }`
 *   instead of silently extracting `.data`.
 *
 * When no envelope is detected, returns the raw payload (optionally transformed).
 */
export function unwrap<T>(responseData: unknown, options: UnwrapOptions = {}): T {
  const { unwrap: doUnwrap = true, transform = true, throwOnEnvelopeError = false } = options;

  let payload = responseData;

  if (doUnwrap && isEnvelope(payload)) {
    if (throwOnEnvelopeError && payload.success === false) {
      const message =
        (payload as { error?: string; message?: string }).error ??
        (payload as { message?: string }).message ??
        "Request failed";
      throw new Error(message);
    }
    // Envelope detected by `success` key alone. If `data` is absent (e.g.
    // 204-style success envelopes, or silent-mode error envelopes without
    // `data: null`), return `undefined` — matches dashboard-shared's
    // `unwrapBody` semantics (the SSoT we're aligning to).
    payload = "data" in payload ? payload.data : undefined;
  }

  if (transform === false) {
    return payload as T;
  }
  const transformer: KeyTransformer = typeof transform === "function" ? transform : snakeToCamelKeys;
  return transformer(payload) as T;
}

/**
 * True when an axios response status indicates a "no body" success — currently
 * HTTP 204 No Content. For these responses, axios with `responseType: 'json'`
 * leaves `response.data === ""` (empty string is not parseable JSON). Callers
 * that try to read `.success` off the empty payload mis-fire — the legacy
 * `if (!data.success) throw` pattern threw on every 204.
 *
 * The shared fetcher methods short-circuit on `isNoBodySuccessStatus(status)`
 * and return `undefined` directly so the bug class can't recur. See origin:
 *   agents/plans/ops_post_mutation_refresh_plan_2026-05-27.md (Phase 3 Item 2).
 */
function isNoBodySuccessStatus(status: number | undefined): boolean {
  return status === 204 || status === 205;
}

/**
 * Configuration for `createUnwrappedFetcher`. Extends `FetcherConfig` with the
 * three unwrap-policy knobs documented on `UnwrapOptions`. All defaults match
 * pre-2026-05-26 behaviour so existing callers (governance, ai, finance, ops,
 * crm, seo, platform, research, outreach, data) continue to work unchanged.
 */
export interface UnwrappedFetcherConfig extends FetcherConfig, UnwrapOptions {}

/**
 * Creates a pre-configured fetcher with envelope unwrap + key transform built in.
 * Each returned method (get, post, put, patch, del) applies the configured unwrap
 * + transform policy to the response body.
 *
 * @example
 * // defaults: unwrap envelope, snake→camel keys, silent on envelope errors
 * const api = createUnwrappedFetcher({
 *   baseURL: import.meta.env.VITE_API_URL ?? "",
 *   withCredentials: true,
 * });
 * const invoices = await api.get<Invoice[]>("/api/invoices");
 *
 * @example
 * // opt out of transform — preserve backend snake_case keys
 * const api = createUnwrappedFetcher({ baseURL: "/api", transform: false });
 *
 * @example
 * // strict error mode — throw on { success: false } responses
 * const api = createUnwrappedFetcher({
 *   baseURL: "/api",
 *   throwOnEnvelopeError: true,
 * });
 */
export function createUnwrappedFetcher(config: UnwrappedFetcherConfig) {
  const { unwrap: doUnwrap, transform, throwOnEnvelopeError, ...fetcherConfig } = config;
  const instance = createFetcher(fetcherConfig);
  const opts: UnwrapOptions = {
    unwrap: doUnwrap,
    transform,
    throwOnEnvelopeError,
  };

  return {
    async get<T>(url: string, axiosConfig?: AxiosRequestConfig): Promise<T> {
      const response = await instance.get(url, axiosConfig);
      if (isNoBodySuccessStatus(response.status)) return undefined as T;
      return unwrap<T>(response.data, opts);
    },
    async post<T>(url: string, body?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
      const response = await instance.post(url, body, axiosConfig);
      if (isNoBodySuccessStatus(response.status)) return undefined as T;
      return unwrap<T>(response.data, opts);
    },
    async put<T>(url: string, body?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
      const response = await instance.put(url, body, axiosConfig);
      if (isNoBodySuccessStatus(response.status)) return undefined as T;
      return unwrap<T>(response.data, opts);
    },
    async patch<T>(url: string, body?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
      const response = await instance.patch(url, body, axiosConfig);
      if (isNoBodySuccessStatus(response.status)) return undefined as T;
      return unwrap<T>(response.data, opts);
    },
    async del<T>(url: string, axiosConfig?: AxiosRequestConfig): Promise<T> {
      const response = await instance.delete(url, axiosConfig);
      if (isNoBodySuccessStatus(response.status)) return undefined as T;
      return unwrap<T>(response.data, opts);
    },
    /** Raw axios instance for advanced use cases */
    instance,
  };
}
